import React from 'react';
import { styled } from 'baseui';
import { Button, SHAPE } from 'baseui/button';
import { FormControl } from 'baseui/form-control';
import { Search } from 'baseui/icon';
import { Input } from 'baseui/input';
import { StyledLink } from 'baseui/link';
import type { Value } from 'baseui/select';
import { Select } from 'baseui/select';
import { DIVIDER, SIZE as TABLE_SIZE, Table } from 'baseui/table-semantic';
import { toaster } from 'baseui/toast';
import { BATCH_VIEW_TABLE_COLUMNS } from '../../consts/batch';
import { useGetBatchInProject } from '../../hooks/use-get-batch-in-project';
import { useUpdateBatch } from '../../hooks/use-update-batch';
import type { BatchStatusT } from '../../types/batch';
import ErrorComponent from './error';
import { TableInnerContainer, TableOuterContainer } from './TableContainer';
const WordBreakDiv = styled('div', () => ({
  wordBreak: 'break-all'
}));
const getBatchStatus = (batchStatus: {
  COMPLETED: number;
  ASSIGNED: number;
  AVAILABLE: number;
  FAILED?: number;
  PAUSED?: number;
}) => {
  const available = `Available: ${batchStatus.AVAILABLE || 0}`;
  const assigned = `Assigned: ${batchStatus.ASSIGNED || 0}`;
  const completed = `Completed: ${batchStatus.COMPLETED || 0}`;
  const failed = `Failed: ${batchStatus.FAILED || 0}`;
  const paused = `Paused: ${batchStatus.PAUSED || 0}`;
  return [available, assigned, paused, completed, failed].join(', ');
};
const Batch = () => {
  const [projectId, setProjectId] = React.useState<number>();
  const [shouldFetch, setShouldFetch] = React.useState(false);
  const priorityOptions: {
    priority: number;
  }[] = [];
  for (let i = 0; i <= 10; i++) {
    priorityOptions.push({
      priority: i
    });
  }
  const {
    data: batches,
    isLoading,
    isError,
    error
  } = useGetBatchInProject(projectId, shouldFetch);
  const {
    updateBatch
  } = useUpdateBatch();
  const handleUpdatePriority = (batch: BatchStatusT, value: Value) => {
    // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
    if (!value || value.length === 0 || value[0].priority === batch.priority) {
      return;
    }
    updateBatch(
    // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
    {
      batchId: batch.batchId,
      priority: +value[0].priority
    }, {
      onSuccess: () => {
        toaster.positive('Updated batch successfully', {
          key: 'updateBatchSuccess'
        });
      },
      onError: error => {
        toaster.negative(`Failed updating batch: ${error.message}`, {
          key: 'updateBatchError'
        });
      }
    });
  };
  return <TableOuterContainer>
      <FormControl label="Project Id">
        <Input value={projectId} onKeyDown={e => {
        if (e.key === 'Enter') {
          setShouldFetch(true);
        }
      }} onChange={e => {
        setShouldFetch(false);
        setProjectId(+e.currentTarget.value);
      }} placeholder="Enter project id" endEnhancer={<Button shape={SHAPE.circle} size="compact" kind="secondary">
              <Search onClick={() => setShouldFetch(true)} />{' '}
            </Button>} type="number" min={0} step={1} />
      </FormControl>
      <TableInnerContainer>
        {isError && <ErrorComponent message={error.message || 'Something went wrong fetching batches'} />}
        {batches && !isError && <Table isLoading={isLoading} emptyMessage={batches.length === 0 ? 'Project or batch not found' : ''} size={TABLE_SIZE.compact} divider={DIVIDER.grid} columns={BATCH_VIEW_TABLE_COLUMNS} data={batches.sort((a, b) => a.batchId - b.batchId).map(batch => {
        return [batch.batchId, <WordBreakDiv key={batch.name}>{batch.name}</WordBreakDiv>, batch.completed ? 'Yes' : 'No', batch.batchSize, batch.resultUrl ? <StyledLink href={batch.resultUrl} target="_blank" rel="noopener noreferrer">
                      Download
                    </StyledLink> : 'Unavailable', <Select key={`${batch.batchId}`} size="compact" clearable={false} searchable={false} options={priorityOptions} labelKey="priority" valueKey="priority" placeholder="Choose priority" maxDropdownHeight="300px" onChange={({
          value
        }) => {
          handleUpdatePriority(batch, value);
        }} value={[{
          priority: batch.priority || 0
        }]} />, getBatchStatus(batch.taskStatusCount.MEDIACONVERSION || {
          ASSIGNED: 0,
          COMPLETED: 0,
          FAILED: 0
        }), getBatchStatus(batch.taskStatusCount.AUTOMATION || {
          ASSIGNED: 0,
          COMPLETED: 0,
          FAILED: 0
        }), getBatchStatus(batch.taskStatusCount.EDIT || {
          ASSIGNED: 0,
          COMPLETED: 0
        }), getBatchStatus(batch.taskStatusCount.REVIEW || {
          ASSIGNED: 0,
          COMPLETED: 0
        }), getBatchStatus(batch.taskStatusCount.METAREVIEW || {
          ASSIGNED: 0,
          COMPLETED: 0
        }), <WordBreakDiv key={batch.inputCsvPath}>{batch.inputCsvPath}</WordBreakDiv>, new Date(batch.createdAt).toLocaleString()];
      })} />}
      </TableInnerContainer>
    </TableOuterContainer>;
};
export default Batch;